import { useCallback, useEffect, useRef, useState } from 'react';
import { appVersionApi } from '@/core/api';

/**
 * Watches for a newer build and reports when this tab is running a stale one.
 *
 * Two independent signals, because a deploy can move either half on its own:
 *
 *   **`/version.json`** — written by the Vite build (see vite.config.ts) and
 *   served by Firebase with `no-store`. Catches a hosting-only deploy, which
 *   is the common case and the one the service worker is worst at noticing.
 *
 *   **`GET /api/v1/app-version`** — the API's Cloud Run revision composed with
 *   an administrator-settable row. Catches an API deploy, and gives Colt a
 *   lever to push the whole network forward without any deploy at all. This is
 *   the "listener on a version-control field" half.
 *
 * Either one differing from what this tab booted with means stale. The first
 * successful read of each is the baseline rather than a compile-time constant
 * for the API side, because the API's build has no reason to match the web
 * app's and never will.
 *
 * Polling rather than SignalR on purpose. The hub is authenticated and only
 * connected once someone is signed in, but a stale *login* page is the worst
 * case of all — the client cannot get far enough to be told anything. Two
 * small unauthenticated GETs a few times an hour is a fair price for that.
 */

/** How often to re-check while the tab is visible. */
const POLL_MS = 5 * 60_000;

/**
 * Ignore checks fired closer together than this. Tab focus and visibility both
 * fire on an alt-tab, and a burst of them should not become a burst of fetches.
 */
const MIN_INTERVAL_MS = 30_000;

export interface AppUpdateState {
  /** True when this tab is running a build the server no longer serves. */
  updateAvailable: boolean;
  /** The server's line about why, when it supplied one. */
  message: string | null;
  /** The server asked for a reload rather than a prompt. */
  forced: boolean;
  /** Re-check now — used by the "check for updates" affordance. */
  check: () => void;
}

/** Reads the build token the hosting is currently serving. */
async function fetchHostedBuild(): Promise<string | null> {
  try {
    // Cache-busted twice over: `no-store` for the HTTP cache, and a query
    // param so no intermediary can answer from a keyed copy. A cached
    // version.json is a version check that can never fire.
    const response = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
    if (!response.ok) return null;
    const body = (await response.json()) as { build?: unknown };
    return typeof body.build === 'string' ? body.build : null;
  } catch {
    // Offline, or a dev server with no version.json. Not knowing is not the
    // same as being stale — say nothing.
    return null;
  }
}

export function useAppUpdate(): AppUpdateState {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [forced, setForced] = useState(false);

  // The API build this tab first saw. Not a compile-time constant: the API and
  // the web app are deployed separately and their tokens never match.
  const apiBaseline = useRef<string | null>(null);
  const lastCheck = useRef(0);

  const check = useCallback(async (force = false) => {
    const now = Date.now();
    if (!force && now - lastCheck.current < MIN_INTERVAL_MS) return;
    lastCheck.current = now;

    const [hosted, api] = await Promise.all([
      fetchHostedBuild(),
      appVersionApi.getAppVersion().catch(() => null),
    ]);

    // `__APP_BUILD__` is injected by Vite. In dev there is no version.json to
    // compare it against, so `hosted` is null and this half stays quiet.
    const hostedStale = hosted !== null && hosted !== __APP_BUILD__;

    let apiStale = false;
    if (api !== null) {
      if (apiBaseline.current === null) {
        apiBaseline.current = api.version;
      } else if (apiBaseline.current !== api.version) {
        apiStale = true;
      }
    }

    if (!hostedStale && !apiStale) return;

    setMessage(api?.message ?? null);
    setForced(api?.forceReload === true && apiStale);
    setUpdateAvailable(true);
  }, []);

  useEffect(() => {
    void check(true);

    const interval = setInterval(() => void check(), POLL_MS);

    // A tab left open all day is the case this exists for, and it is also the
    // case a timer alone handles badly: browsers throttle timers in background
    // tabs, so the check that matters is the one when someone comes back to it.
    const onWake = () => {
      if (document.visibilityState === 'visible') void check();
    };
    document.addEventListener('visibilitychange', onWake);
    window.addEventListener('focus', onWake);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onWake);
      window.removeEventListener('focus', onWake);
    };
  }, [check]);

  return {
    updateAvailable,
    message,
    forced,
    check: useCallback(() => void check(true), [check]),
  };
}
